import { useQuery } from '@apollo/client'

import { ADVERTISED_PROMOTIONS_QUERY } from '../../../../graphql/promotions'
import { AdvertisedPromotion } from '../../../../types/cart'

/**
 * The offers this cart is eligible for.
 *
 * `errorPolicy: 'all'` is load-bearing rather than incidental. A failed or partially failed
 * lookup must not block the code field: a booker who knows their code can still use it, and
 * the list is an aid rather than the mechanism. Dropping to 'none' would discard the offers
 * that did arrive alongside the error.
 *
 * Always returns an array, so no caller has to decide what an absent list means.
 *
 * `failed` is reported separately BECAUSE of errorPolicy: 'all'. Swallowing the error keeps
 * the code field alive, which is right, but it also makes a failed lookup and a genuinely
 * empty list render identically — and those mean opposite things to an operator deciding
 * what to offer a guest. The caller says so on screen rather than letting "no offers exist"
 * be inferred from a network problem.
 */
export function useAdvertisedOffers(cartID: string): {
  offers: AdvertisedPromotion[]
  loading: boolean
  failed: boolean
} {
  const { data, loading, error } = useQuery<{
    advertisedPromotions: AdvertisedPromotion[]
  }>(ADVERTISED_PROMOTIONS_QUERY, {
    variables: { cartID },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all'
  })

  return {
    offers: data?.advertisedPromotions ?? [],
    loading,
    failed: Boolean(error)
  }
}
